import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface ScheduleData {
  scheduleType: string;
  startDate?: Date;
  startTime: string;
  endTime: string;
  timezone: string;
  dayOfWeek?: string;
  customSchedule?: string;
}

interface AdvancedSchedulePickerProps {
  value: ScheduleData;
  onChange: (schedule: ScheduleData) => void;
  className?: string;
}

const US_TIMEZONES = [
  { value: 'EST', label: 'Eastern (EST/EDT)' },
  { value: 'CST', label: 'Central (CST/CDT)' },
  { value: 'MST', label: 'Mountain (MST/MDT)' },
  { value: 'PST', label: 'Pacific (PST/PDT)' },
  { value: 'AKST', label: 'Alaska (AKST/AKDT)' },
  { value: 'HST', label: 'Hawaii (HST)' }
];

const SCHEDULE_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'weekends', label: 'Weekends Only' },
  { value: 'specific-date', label: 'Specific Date' },
  { value: 'custom', label: 'Custom Schedule' }
];

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
];

export const AdvancedSchedulePicker: React.FC<AdvancedSchedulePickerProps> = ({
  value,
  onChange,
  className = ""
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const updateSchedule = (updates: Partial<ScheduleData>) => {
    onChange({ ...value, ...updates });
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const time12 = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        times.push({ value: time24, label: time12 });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const renderScheduleSpecificFields = () => {
    switch (value.scheduleType) {
      case 'weekly':
      case 'bi-weekly':
        return (
          <div>
            <Label htmlFor="dayOfWeek">Day of Week</Label>
            <Select 
              value={value.dayOfWeek || ''} 
              onValueChange={(day) => updateSchedule({ dayOfWeek: day })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OF_WEEK.map((day) => (
                  <SelectItem key={day.value} value={day.value}>
                    {day.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'monthly':
        return (
          <div>
            <Label htmlFor="monthlyDate">Monthly Date</Label>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !value.startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value.startDate ? format(value.startDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={value.startDate}
                  onSelect={(date) => {
                    updateSchedule({ startDate: date });
                    setIsCalendarOpen(false);
                  }}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        );

      case 'yearly':
        return (
          <div>
            <Label htmlFor="yearlyDate">Annual Date</Label>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !value.startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value.startDate ? format(value.startDate, "MMMM do") : <span>Pick date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={value.startDate}
                  onSelect={(date) => {
                    updateSchedule({ startDate: date });
                    setIsCalendarOpen(false);
                  }}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        );

      case 'specific-date':
        return (
          <div>
            <Label htmlFor="specificDate">Specific Date</Label>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !value.startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value.startDate ? format(value.startDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={value.startDate}
                  onSelect={(date) => {
                    updateSchedule({ startDate: date });
                    setIsCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        );

      case 'custom':
        return (
          <div>
            <Label htmlFor="customSchedule">Custom Schedule</Label>
            <Input
              id="customSchedule"
              value={value.customSchedule || ''}
              onChange={(e) => updateSchedule({ customSchedule: e.target.value })}
              placeholder="e.g., First and third Monday of each month"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <Label htmlFor="scheduleType">Schedule Type</Label>
        <Select 
          value={value.scheduleType} 
          onValueChange={(type) => updateSchedule({ scheduleType: type })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select schedule type" />
          </SelectTrigger>
          <SelectContent>
            {SCHEDULE_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {renderScheduleSpecificFields()}

      {value.scheduleType !== 'custom' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="startTime">Start Time</Label>
            <Select 
              value={value.startTime} 
              onValueChange={(time) => updateSchedule({ startTime: time })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Start time" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timeOptions.map((time) => (
                  <SelectItem key={time.value} value={time.value}>
                    {time.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="endTime">End Time</Label>
            <Select 
              value={value.endTime} 
              onValueChange={(time) => updateSchedule({ endTime: time })}
            >
              <SelectTrigger>
                <SelectValue placeholder="End time" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timeOptions.map((time) => (
                  <SelectItem key={time.value} value={time.value}>
                    {time.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="timezone">Timezone</Label>
        <Select 
          value={value.timezone} 
          onValueChange={(timezone) => updateSchedule({ timezone })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {US_TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};